"use client";

import React, { useEffect, useRef, useState } from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function ContactInformation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="122971" id="contact-details" className="w-full bg-background py-16 lg:py-24">
      <div ref={ref} className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 overflow-hidden rounded-sm shadow-xl lg:grid-cols-2 border border-border/50">
          
          {}
          <div className="flex flex-col justify-between bg-card p-10 lg:p-16">
            <div className="mb-16">
              <img src="https://vs-group.dk/wp-content/uploads/2026/03/VSG-logo.png" alt="VS Group Logo" className="object-contain !w-[450px] !h-[200px]" />
            </div>

            <div className="flex flex-col gap-10 mt-auto">
              {}
              <div className="flex items-start gap-5 p-6 rounded-sm shadow-xl bg-card border-b-2 border-transparent hover:border-accent hover:-translate-y-1 transition-all duration-300">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent/10">
                  <Phone className="h-5 w-5 text-accent stroke-[1.5]" />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-widest text-accent/80">
                    Direct Channels
                  </span>
                  <Link to="tel:+4551194045" className="font-sans text-lg text-foreground transition-colors hover:text-accent">+45 51 19 40 45</Link>
                  <Link to="tel:+4529772415" className="font-sans text-lg text-foreground transition-colors hover:text-accent">+45 29 77 24 15</Link>
                </div>
              </div>

              {}
              <div className="flex items-start gap-5 p-6 rounded-sm shadow-xl bg-card border-b-2 border-transparent hover:border-accent hover:-translate-y-1 transition-all duration-300">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent/10">
                  <Mail className="h-5 w-5 text-accent stroke-[1.5]" />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-widest text-accent/80">
                    Email Support
                  </span>
                  <Link to="mailto:info@vsgroup.com" className="font-sans text-lg text-foreground transition-colors hover:text-accent">info@vs-group.com</Link>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="flex flex-col bg-card p-10 lg:p-16 border-l border-border/50 !bg-[url(https://vs-group.dk/wp-content/uploads/2026/03/IMG_20171121_122257-scaled-e1774171912303.jpg)]">
            <div className="mb-8 flex items-start gap-4 p-6 rounded-sm shadow-xl bg-card border-b-2 border-transparent hover:border-accent hover:-translate-y-1 transition-all duration-300">
              <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/10">
                <MapPin className="h-5 w-5 text-accent stroke-[1.5]" />
              </div>
              <div>
                <h3 className="font-sans text-3xl font-bold text-foreground tracking-tight">
                  Office Headquarters
                </h3>
                <p className="mt-4 max-w-sm font-sans text-base leading-relaxed text-foreground/80">Møllevej 26C 5300 Kerteminde DK&nbsp;<br /></p>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="mt-12 px-4 lg:px-8">
          <Separator className="mb-6 bg-foreground/10" />
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} VS Group. All rights reserved.
            </p>
            <p className="text-sm text-muted-foreground">
              Designed for industrial excellence.
            </p>
          </div>
        </div>
      </div>
    </section>;
}